import { useFrame, useThree } from "@react-three/fiber";
import { useRef } from "react";
import {
  AmbientLight,
  type Camera,
  DirectionalLight,
  HemisphereLight,
  Vector3,
} from "three";

// Scale-invariant lights (ambient, hemisphere, directional, env IBL).
// Edge readability: camera-right key always lights the viewer's right side more
// than left/front/back so 90° profile corners stay visible while orbiting.
export const PRODUCT_VIEWER_LIGHTING = {
  ambient: {
    intensity: 3.5,
  },
  hemisphere: {
    intensity: 0.6,
    groundColor: "white",
  },
  topLight: {
    position: [0, 100, 0] as const,
    intensity: 1,
  },
  // Main key — fixed to camera right, targets look-at point on forward axis.
  cameraRightKey: {
    intensity: 2.2,
    rightOffset: 120,
    upOffset: 35,
    targetDistance: 100,
  },
  // Soft lift on camera-left so shadow side is not pure black.
  cameraLeftFill: {
    intensity: 0.2,
    leftOffset: 90,
    upOffset: 20,
    targetDistance: 100,
  },
} as const;

const cameraForward = new Vector3();
const cameraRight = new Vector3();
const cameraUp = new Vector3();
const cameraTarget = new Vector3();
const lightPosition = new Vector3();

export function syncCameraOffsetDirectionalLight(
  light: DirectionalLight | null | undefined,
  camera: Camera,
  offsets: {
    rightOffset?: number;
    leftOffset?: number;
    upOffset?: number;
    targetDistance: number;
  },
) {
  if (!light) return;
  cameraRight.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
  cameraUp.setFromMatrixColumn(camera.matrixWorld, 1).normalize();
  lightPosition.copy(camera.position);
  if (offsets.rightOffset) {
    lightPosition.addScaledVector(cameraRight, offsets.rightOffset);
  }
  if (offsets.leftOffset) {
    lightPosition.addScaledVector(cameraRight, -offsets.leftOffset);
  }
  if (offsets.upOffset) {
    lightPosition.addScaledVector(cameraUp, offsets.upOffset);
  }
  light.position.copy(lightPosition);
  camera.getWorldDirection(cameraForward);
  cameraTarget
    .copy(camera.position)
    .addScaledVector(cameraForward, offsets.targetDistance);
  light.target.position.copy(cameraTarget);
  light.target.updateMatrixWorld();
}

/** Primary key from viewer's right — right-facing surfaces brighter than left. */
export const CameraRightKeyLight = ({
  intensity = PRODUCT_VIEWER_LIGHTING.cameraRightKey.intensity,
}) => {
  const lightRef = useRef<DirectionalLight>(null);
  const { camera } = useThree();
  const config = PRODUCT_VIEWER_LIGHTING.cameraRightKey;

  useFrame(() => {
    syncCameraOffsetDirectionalLight(lightRef.current, camera, config);
  });

  return (
    <directionalLight ref={lightRef} intensity={intensity} castShadow={false} />
  );
};

/** Subtle fill from viewer's left — keeps shadow side readable. */
export const CameraLeftFillLight = ({
  intensity = PRODUCT_VIEWER_LIGHTING.cameraLeftFill.intensity,
}) => {
  const lightRef = useRef<DirectionalLight>(null);
  const { camera } = useThree();
  const config = PRODUCT_VIEWER_LIGHTING.cameraLeftFill;

  useFrame(() => {
    syncCameraOffsetDirectionalLight(lightRef.current, camera, config);
  });

  return (
    <directionalLight ref={lightRef} intensity={intensity} castShadow={false} />
  );
};

export const ProductViewerLighting = () => (
  <>
    <ambientLight intensity={PRODUCT_VIEWER_LIGHTING.ambient.intensity} />
    <hemisphereLight
      intensity={PRODUCT_VIEWER_LIGHTING.hemisphere.intensity}
      groundColor={PRODUCT_VIEWER_LIGHTING.hemisphere.groundColor}
    />
    <directionalLight
      position={[...PRODUCT_VIEWER_LIGHTING.topLight.position]}
      intensity={PRODUCT_VIEWER_LIGHTING.topLight.intensity}
    />
    <CameraRightKeyLight />
    <CameraLeftFillLight />
  </>
);

export function createProductViewerLights() {
  const ambient = new AmbientLight(
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.ambient.intensity,
  );

  const hemisphere = new HemisphereLight(
    0xffffff,
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.hemisphere.intensity,
  );

  const topLight = new DirectionalLight(
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.topLight.intensity,
  );
  topLight.position.set(...PRODUCT_VIEWER_LIGHTING.topLight.position);

  const cameraRightKey = new DirectionalLight(
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.cameraRightKey.intensity,
  );
  cameraRightKey.castShadow = false;

  const cameraLeftFill = new DirectionalLight(
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.cameraLeftFill.intensity,
  );
  cameraLeftFill.castShadow = false;

  return {
    lights: [
      ambient,
      hemisphere,
      topLight,
      cameraRightKey,
      cameraRightKey.target,
      cameraLeftFill,
      cameraLeftFill.target,
    ],
    cameraRightKey,
    cameraLeftFill,
  };
}
